import getLocal from '@/app/utils/getLocal';
import { useLang } from '@/app/utils/useLang';
import gtm from '@/utils/gtm';
import { useGlobal } from '@/utils/useGlobal';
import EfLink from '@elem/ui/ef-link';
import { FormatMoneyLocal } from 'eco-design-micro/price/FormatMoneyLocal';
import EcoLink from 'eco-design/base/eco-link';
import { getShopifyProductList } from 'lowcode/apis/getShopifyProductList';
import EditorImg from 'lowcode/components/EditorImg';
import EditorLink from 'lowcode/components/EditorLink';
import { FC, useEffect, useRef, useState } from 'react';
import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { IProductItem, products } from './config';
import { productsAc } from './configAc';
import { productsUltra } from './configUltra';
import DutyFreeModal from './DutyFreeModal2';
import { IconMore, IconRight } from './IconMore';
import styled from './index.module.scss';
import LastSwiperItem from './LastSwiperItem';
import lastSwiperItemStyle from './LastSwiperItem/index.module.scss';
import Choose from './LastSwiperItem/Choose';

type IType = 'ac' | 'ultra';
type IProps = {
    type?: IType;
};

// 三个页面产品列表不同
const productMap: Record<IType, Record<string, IProductItem[]>> = {
    ac: productsAc,
    ultra: productsUltra,
};

const detailsLinkMap = {
    ac: 'stream-ac-pro-ac',
    ultra: 'stream-ultra-pro',
};
//eu.ecoflow.com/products/stream-series-plug-in-solar-battery?variant=50396945482071
//eu.ecoflow.com/products/?variant=50510963016023
//eu.ecoflow.com/products/?variant=50510963605847
const ProductList: FC<IProps> = ({ type }) => {
    const { locale } = getLocal();
    const { isMobile } = useGlobal();
    const { t } = useLang('theProduct.productlist');
    const { t: _t } = useLang('theProduct.overview');
    const productList = type ? productMap[type] : products;
    const [list, setList] = useState(productList[locale] || productList.eu);
    const [chooseItem, setChooseItem] = useState<null | IProductItem>(null);
    const swiperRef = useRef<any>(null);
    const variantIds = list?.map((item) => item.variantId);
    const [spaceBetween, setSpaceBetween] = useState(24);

    const productlist_title = type
        ? {
              ac: t('productlist_title4'),
              ultra: t('productlist_title3'),
          }[type]
        : t('productlist_title2');

    const detailsPath = type ? detailsLinkMap[type] : 'stream-series-plug-in-solar-battery';
    // 埋点区分导航栏名称
    const navName = type
        ? {
              ac: _t('overview_name3'),
              ultra: _t('overview_name2'),
          }[type]
        : _t('overview_name1');

    const initProductList = () => {
        getShopifyProductList(locale, variantIds).then((res) => {
            // 混入img productTitle price等参数
            const newList = list.map((item) => {
                const target = res.find((resItem: any) => resItem.variantId === item.variantId);
                return {
                    ...item,
                    img: target?.variantImg,
                    price: target?.price,
                    compareAtPrice: target?.compareAtPrice,
                    productTitle: target?.skuTitle,
                    // detailsLink: `https://${locale}.ecoflow.com/products/${detailsPath}?variant=${item.variantId}`,
                    detailsLink: type
                        ? `https://${locale}.ecoflow.com/products/${detailsPath}?variant=${item.variantId}`
                        : item.detailsLink || `https://${locale}.ecoflow.com/pages/${detailsPath}#${item.variantId}`,
                };
            });
            setList(newList);
        });
    };
    const handleClick = (item: IProductItem) => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'checkout',
            button_name: `${t('productlist_btn2')}_${item.name || item.productTitle}`,
        });
        if (locale === 'de') {
            setChooseItem(item);
        } else {
            window.open(`https://${locale}.ecoflow.com/cart/${item.variantId}:1?channel=buy_button`, '_blank');
        }
    };

    const handlePrev = () => {
        swiperRef.current?.slidePrev();
        gtm.push({
            event: 'uaEvent',
            event_name: 'product_preview_page',
            list_name: t('productlist_title'),
            button_name: 'Left switch',
        });
    };

    const handleNext = () => {
        swiperRef.current?.slideNext();
        gtm.push({
            event: 'uaEvent',
            event_name: 'product_preview_page',
            list_name: t('productlist_title'),
            button_name: 'Right switch',
        });
    };

    const getDescText = (index: number, noPanels?: boolean) => {
        if (index === 1) {
            return noPanels ? (type === 'ac' ? t('productlist_desc7') : t('productlist_desc5')) : t('productlist_desc2');
        }
        if (index === 2) {
            return noPanels ? t('productlist_desc6') : t('productlist_desc3');
        }
        return noPanels ? t('productlist_desc4') : t('productlist_desc1');
    };

    useEffect(() => {
        initProductList();
        if (typeof window !== 'undefined') {
            const windowWidth = isMobile ? window.innerWidth : document.documentElement.scrollWidth;

            if (windowWidth < 1024) {
                setSpaceBetween(16);
            } else {
                setSpaceBetween(24);
            }
        }
    }, []);

    return (
        <div
            className={`${styled.ProductList} GtmModuleData-section`}
            data-section-id="ProductListStatusB"
            data-section-title={t('rights_title')}
            id="Products"
        >
            <p className="title">{productlist_title}</p>
            {!type && <p className="subtitle">{t('productlist_subtitle')}</p>}

            <p className="choose-link">
                <Choose className="choose-icon" />
                <EfLink href={`https://www.ecoflow.com/${locale}/stream-series-energy-saving-calculator`} target="_blank">
                    {t('productlist_choose')}
                </EfLink>
            </p>

            <div className="product_central">
                <Swiper
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                    }}
                    className="product_box"
                    modules={[Navigation]}
                    spaceBetween={spaceBetween}
                    navigation={{
                        nextEl: '.swiper-button-next',
                        prevEl: '.swiper-button-prev',
                    }}
                    // centeredSlides={true}
                    slidesPerView={'auto'}
                    initialSlide={0}
                    scrollbar={{
                        hide: false,
                        draggable: true,
                        dragSize: 32,
                        snapOnRelease: true,
                    }}
                >
                    {list.map((item, index) => (
                        <SwiperSlide className="product_item" key={item.variantId + index}>
                            {item.tips && <span className="tips">{item.tips}</span>}
                            <div className="product-top">
                                <EcoLink className="img-link" href={''}>
                                    <EditorImg className="img" pc={item.img} alt={item.productTitle} />
                                </EcoLink>
                                <EcoLink href={''}>
                                    <p className="name">{item.name || item.productTitle}</p>
                                </EcoLink>
                                {/* <p className="desc">{item.desc}</p> */}
                            </div>
                            <div className="product-bottom">
                                {type !== 'ac' && (
                                    <>
                                        {/* <div className={`save ${item.noPanels ? 'max-100' : ''}`}>
                                            <span>{item.save}</span>
                                        </div> */}
                                        <div className="product-desc">
                                            {item.descList.map((text, index) => {
                                                return (
                                                    <div key={text} className="product-desc__item">
                                                        <div className="item-title">{text}</div>
                                                        <div className="item-desc">{getDescText(index, item.noPanels)}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                                <div className="price-line">
                                    <span className="price">{FormatMoneyLocal(item.price || 0, locale)}</span>
                                    {Number(item.compareAtPrice) > Number(item.price) ? (
                                        <span className="compare-price">{FormatMoneyLocal(item.compareAtPrice || 0, locale)}</span>
                                    ) : null}
                                </div>
                                {/* <div className="message">{item.message}</div> */}
                                <div className="tools">
                                    {/* <EditorLink className="btn" onClick={() => handleClick(item)}>
                                        {t('productlist_btn2')}
                                    </EditorLink> */}
                                    <EditorLink
                                        className="btn"
                                        dataLayer={{
                                            event: 'uaEvent',
                                            event_name: 'buy',
                                            product_name: item.productTitle,
                                            position: `Flash Sale_${navName}_${item.productTitle}_${t('productlist_btn3')}`,
                                        }}
                                        href={item.detailsLink}
                                        target="_blank"
                                        // onClick={() => handleDetail(item)}
                                    >
                                        {t('productlist_btn3')}
                                    </EditorLink>
                                </div>
                            </div>
                        </SwiperSlide>
                    ))}
                    <SwiperSlide className={lastSwiperItemStyle.LastSwiperItem}>
                        <LastSwiperItem />
                    </SwiperSlide>

                    {/* {!isEu && (
                        <SwiperSlide className="product_item">
                            <EfLink
                                href={`https://${locale}.ecoflow.com/pages/stream-series-plug-in-solar-battery`}
                                dataLayer={{
                                    event: 'uaEvent',
                                    event_name: 'buy',
                                    product_name: '',
                                    position: `${productlist_title}_${t('productlist_viewall')}`,
                                }}
                                target="_blank"
                                className="viewall-btn"
                            >
                                {t('productlist_viewall')}
                                <IconMore />
                            </EfLink>
                        </SwiperSlide>
                    )} */}

                    <div className="swiper-button-prev" onClick={handlePrev} />
                    <div className="swiper-button-next" onClick={handleNext} />
                </Swiper>
            </div>
            <DutyFreeModal item={chooseItem} show={chooseItem !== null} close={() => setChooseItem(null)} />
        </div>
    );
};

export default ProductList;
